"use client"

import { useState } from "react"
import Link from "next/link"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// ---------- Sides ----------
// Sources: Hoshizaki, Manitowoc Ice, Scotsman, GE Profile Opal, and FirstBuild
// (Opal) published product specifications for nugget vs. cube/cubelet ice
// machines. Energy/throughput notes reference ENERGY STAR commercial ice
// machine criteria where applicable.

interface Side {
  key: "nugget" | "cube"
  name: string
  tag: string
  icon: string
  color: string
  pros: string[]
  cons: string[]
}

const SIDE_NUGGET: Side = {
  key: "nugget",
  name: "Nugget ice",
  tag: "The chewable pick",
  icon: "◉",
  color: "#0469f0",
  pros: [
    "Soft and chewable — the 'Sonic ice' texture",
    "Absorbs flavor — better with sodas, iced coffee, mixed drinks",
    "Quieter clinking in the glass",
    "Packs tightly with fewer air gaps",
  ],
  cons: [
    "Melts faster — more dilution",
    "Countertop nugget machines $400–$650; commercial $2,000+",
    "Reduces carbonation faster than cube",
    "Higher per-pound energy use than equivalent cube",
  ],
}

const SIDE_CUBE: Side = {
  key: "cube",
  name: "Cube ice",
  tag: "The cocktail pick",
  icon: "▣",
  color: "#ff9951",
  pros: [
    "Slow-melting — minimal drink dilution",
    "Crystal-clear when produced on a plate evaporator",
    "Holds carbonation longer in soda and sparkling drinks",
    "Standard on most under-counter and built-in refrigerator ice makers",
  ],
  cons: [
    "Hard texture — not for chewing",
    "Loud clinking",
    "Larger air gaps in storage bins",
    "Less surface area means slower initial chill",
  ],
}

interface Scenario {
  who: string
  /** First-person form of the same scenario, for the interactive picker. */
  pick: string
  winner: "nugget" | "cube"
  reason: string
}

const SCENARIOS: Scenario[] = [
  {
    who: "Cocktail bar / whiskey at home",
    pick: "I make cocktails or drink whiskey on the rocks",
    winner: "cube",
    reason: "Slow melt preserves the spirit's proof and flavor. Clear cubes look right in a rocks glass.",
  },
  {
    who: "Sodas, sweet tea, iced coffee",
    pick: "Sodas, sweet tea, or iced coffee are my everyday drinks",
    winner: "nugget",
    reason: "Porous structure absorbs flavor instead of fighting it — the reason Sonic and Chick-fil-A use nugget.",
  },
  {
    who: "Sparkling water household",
    pick: "Sparkling water is a staple in my house",
    winner: "cube",
    reason: "Less surface area = slower fizz loss. Nugget kills carbonation in minutes.",
  },
  {
    who: "Healthcare / pediatric setting",
    pick: "Someone here chews their ice (or needs soft ice)",
    winner: "nugget",
    reason: "Soft, chewable texture is hospital-standard for swallow-impaired patients and post-op hydration.",
  },
  {
    who: "High-volume restaurant or bar",
    pick: "I need serious volume — parties, a bar, a crowd",
    winner: "cube",
    reason: "Commercial cube machines from Hoshizaki and Manitowoc lead on production-per-watt and bin storage density.",
  },
  {
    who: "Smoothies and frozen drinks",
    pick: "Smoothies and blended drinks happen weekly",
    winner: "nugget",
    reason: "Soft nugget blends smoothly without chipping blades — every blender manual recommends crushed/nugget over cube.",
  },
]

// ---------- Interactive picker (FRIDGE-578: "make it also be a tool") ----------
// Derives entirely from SCENARIOS so the quiz and the printed verdicts can
// never disagree. No new claims are introduced — selecting a statement adds
// its scenario's published verdict to the tally.

function IcePicker() {
  const [picked, setPicked] = useState<Set<number>>(new Set())

  const toggle = (i: number) =>
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })

  const nugget = [...picked].filter((i) => SCENARIOS[i].winner === "nugget").length
  const cube = [...picked].filter((i) => SCENARIOS[i].winner === "cube").length
  const total = nugget + cube
  const verdict: "nugget" | "cube" | "tie" | null =
    total === 0 ? null : nugget === cube ? "tie" : nugget > cube ? "nugget" : "cube"
  const winReasons =
    verdict === "nugget" || verdict === "cube"
      ? [...picked].filter((i) => SCENARIOS[i].winner === verdict).map((i) => SCENARIOS[i].reason)
      : []

  return (
    <section className="bg-white py-8 md:py-12 border-t border-gray-200">
      <div className="max-w-[960px] mx-auto px-6 md:px-8">
        <SectionRule eyebrow="The picker" title="Tap what's true for you" right="Live verdict below" />

        <div className="grid sm:grid-cols-2 gap-2.5" role="group" aria-label="Which of these apply to you?">
          {SCENARIOS.map((s, i) => {
            const on = picked.has(i)
            const color = s.winner === "nugget" ? "#0469f0" : "#ff9951"
            return (
              <button
                key={s.pick}
                type="button"
                aria-pressed={on}
                aria-label={s.pick}
                onClick={() => toggle(i)}
                className="text-left rounded-xl px-4 py-3.5 border transition-all"
                style={
                  on
                    ? { borderColor: color, background: `${color}0d`, boxShadow: `inset 0 0 0 1px ${color}` }
                    : { borderColor: "#e5e7eb", background: "#fff" }
                }
              >
                <span className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-black text-white"
                    style={on ? { background: color, borderColor: color } : { borderColor: "#d1d5db", color: "#fff" }}
                  >
                    {on ? "✓" : ""}
                  </span>
                  <span className="text-[14px] font-semibold leading-[1.35] text-gray-900">{s.pick}</span>
                </span>
              </button>
            )
          })}
        </div>

        {/* Live tally + verdict */}
        <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5 md:p-6" aria-live="polite">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 mb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0469f0] w-16">Nugget</span>
            <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: total ? `${(nugget / total) * 100}%` : "0%", background: "#0469f0" }}
              />
            </div>
            <span className="font-mono text-[13px] font-bold text-gray-900 w-5 text-right">{nugget}</span>
          </div>
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#ff9951] w-16">Cube</span>
            <div className="h-2.5 rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: total ? `${(cube / total) * 100}%` : "0%", background: "#ff9951" }}
              />
            </div>
            <span className="font-mono text-[13px] font-bold text-gray-900 w-5 text-right">{cube}</span>
          </div>

          <div className="mt-5 pt-5 border-t border-gray-200">
            {verdict === null && (
              <p className="text-[14px] text-gray-500">
                Tap the statements that sound like your household — the verdict updates as you go.
              </p>
            )}
            {verdict === "tie" && (
              <div>
                <div className="text-[20px] font-black tracking-tight text-gray-900">Dead even — you&apos;re a two-ice household.</div>
                <p className="text-[13px] text-gray-600 mt-1.5 leading-[1.5]">
                  A built-in cube maker for drinks that need slow melt, plus a countertop nugget
                  machine for everything chewable, is a real and common setup.
                </p>
              </div>
            )}
            {(verdict === "nugget" || verdict === "cube") && (
              <div>
                <div className="text-[20px] font-black tracking-tight text-gray-900">
                  Your ice is{" "}
                  <span style={{ color: verdict === "nugget" ? "#0469f0" : "#ff9951" }}>
                    {verdict === "nugget" ? "nugget" : "cube"}
                  </span>
                  <span className="text-gray-500 font-bold text-[14px] ml-2">
                    {verdict === "nugget" ? nugget : cube} of {total} picks
                  </span>
                </div>
                <ul className="mt-2 space-y-1 text-[13px] text-gray-600 leading-[1.5]">
                  {winReasons.slice(0, 3).map((r) => (
                    <li key={r}>— {r}</li>
                  ))}
                </ul>
                <Link
                  href="/ice-makers"
                  className="inline-flex items-center gap-1.5 mt-4 px-5 py-2.5 rounded-full font-bold text-[13px] text-white transition-opacity hover:opacity-90"
                  style={{ background: verdict === "nugget" ? "#0469f0" : "#ff9951" }}
                >
                  Browse {verdict === "nugget" ? "nugget" : "cube"} ice makers →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ---------- Page ----------

export function NuggetVsCubeIceComparisonPageClient() {
  const [tab, setTab] = useState<"nugget" | "cube">("nugget")

  return (
    <ToolPageShell
      toolName="Nugget vs Cube Ice Maker Comparison"
      heroEyebrow="Ice & specialty"
      heroTitlePre="Nugget"
      heroTitleItalic="vs"
      heroTitlePost="cube ice."
      heroSubtitle="Same machine category, two completely different drinks experiences. Soft, chewable nugget for sodas and chewing — slow-melting cube for cocktails and carbonation."
      heroMeta={["⏱ ~3 min read", "Hoshizaki + Manitowoc + GE Profile spec data"]}
      heroShare={<ToolShareBar title="Nugget vs Cube Ice Maker Comparison" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div
              className="font-serif text-[#ff9951] leading-none flex-shrink-0"
              style={{ fontSize: 40, letterSpacing: "-0.05em" }}
            >
              &ldquo;
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-2 text-[#ff9951]">
                The short answer
              </div>
              <p
                className="font-medium text-gray-900"
                style={{
                  fontSize: 16,
                  lineHeight: 1.5,
                  letterSpacing: "-0.01em",
                  textWrap: "pretty" as never,
                }}
              >
                Pick <em className="italic text-[#ff9951] font-bold">nugget</em> if you drink sodas,
                iced coffee, or chew your ice. Pick{" "}
                <em className="italic text-[#ff9951] font-bold">cube</em> if you make cocktails,
                drink whiskey, or want sparkling drinks to stay sparkling.
              </p>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Why do people love nugget ice so much?",
          answer:
            "It's soft enough to chew without dental risk, and its porous structure absorbs whatever drink it's in — so a soda tastes more like soda, not like watered-down soda. The cult following dates to Sonic Drive-In, which has used nugget since the 1950s.",
        },
        {
          question: "Is nugget ice actually fast to melt?",
          answer:
            "Yes — much faster than a solid cube of equivalent weight, because nugget has dramatically more surface area per gram. That's the same property that makes it absorb flavor.",
        },
        {
          question: "Are countertop nugget machines (GE Profile Opal) reliable?",
          answer:
            "Manufacturer-published throughput is 24 lb/day on the Opal 2.0. Long-term reliability depends heavily on water quality — manufacturers including FirstBuild publish a recommended quarterly cleaning cycle that materially affects unit life.",
        },
        {
          question: "Does cube ice really keep drinks colder?",
          answer:
            "For sustained cooling, yes. A larger frozen mass with less surface area absorbs heat more slowly, so the drink stays cold longer with less water added. For instant chill, nugget wins because of surface contact.",
        },
        {
          question: "Which uses less energy?",
          answer:
            "Per pound of ice produced, modern commercial cube machines from Hoshizaki and Manitowoc generally lead the ENERGY STAR commercial ice machine spec. Countertop nugget appliances have looser efficiency standards and tend to use more kWh per pound of ice.",
        },
      ]}
      relatedTools={[
        { title: "Ice Maker Capacity Calculator", category: "Size & Capacity", description: "Match production to demand.", href: "/tools/ice-maker-capacity-calculator", time: "~1 min" },
        { title: "Find Your Fridge", category: "Quiz", description: "Five questions to a starting product group.", href: "/tools/find-your-fridge", time: "~90 sec" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "Annual cost for an ice maker.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Size Calculator", category: "Size & Capacity", description: "Right cu ft for your household.", href: "/tools/refrigerator-size-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Ready to shop ice makers?",
        body: "Browse countertop nugget makers and full-size cube ice makers from every major retailer.",
        buttonLabel: "Browse ice makers",
        buttonHref: "/ice-makers",
      }}
    >
      {/* ============ SPEC FIGHT ============ */}
      <section className="bg-white pt-8 md:pt-12">
        <div className="max-w-[880px] mx-auto px-6 md:px-8">
          <GuideTool
            eyebrow="Interactive selector"
            title="Nugget or cube \u2014 which ice are you?"
            intro="Two questions on how the ice actually gets used."
            questions={[
                          {
                            id: "use",
                            label: "Where does most of the ice go?",
                            options: [
                              { value: "cocktails", label: "Cocktails & entertaining" },
                              { value: "chew", label: "Drinks you chew the ice from" },
                              { value: "bulk", label: "Coolers & bulk chilling" }
                            ],
                          },
                          {
                            id: "priority",
                            label: "What matters most?",
                            options: [
                              { value: "slow", label: "Slow melt, low dilution" },
                              { value: "soft", label: "Soft, chewable texture" },
                              { value: "cheap", label: "Lowest cost per pound" }
                            ],
                          }
            ]}
            compute={(a) => {
                          if (a.use === "chew" || a.priority === "soft") {
                            return { tone: "good", verdict: "Nugget ice", detail: "Chewable texture is the whole nugget proposition \u2014 compacted flake absorbs the drink and stays soft. You accept faster melt and a higher machine price for it.", action: { label: "See nugget-capable ice makers", href: "/ice-makers" } }
                          }
                          if (a.use === "cocktails" || a.priority === "slow") {
                            return { tone: "good", verdict: "Cube ice", detail: "Dense clear cubes melt slowest and dilute least \u2014 the cocktail and dinner-table pick \u2014 and cube machines run cheaper per pound of ice than nugget makers.", action: { label: "See cube ice makers", href: "/ice-makers" } }
                          }
                          return { tone: "good", verdict: "Cubes \u2014 and lots of them", detail: "For coolers and bulk chilling, cube machines win on output per dollar and the ice holds longer in the chest. Nugget's texture premium buys nothing in a cooler." }
                        }}
            ctaLabel="Pick my ice"
            footnote="Texture and melt trade-offs are detailed in the spec fight below."
          />
        </div>
      </section>

      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Side by side" title="The spec fight" right="Ice texture, melt rate, dilution" />

          <div className="grid md:grid-cols-2 gap-5">
            {[SIDE_NUGGET, SIDE_CUBE].map((s, i) => (
              <div
                key={s.name}
                className="rounded-xl p-6 md:p-7"
                style={{
                  border: `2px solid ${s.color}`,
                  background: i === 0 ? "rgba(4,105,240,0.03)" : "rgba(255,153,81,0.03)",
                }}
              >
                <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: `${s.color}30` }}>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{ color: s.color }}>
                      {i === 0 ? "Side A" : "Side B"}
                    </div>
                    <div className="text-[26px] font-black tracking-tight text-gray-900">{s.name}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">{s.tag}</div>
                  </div>
                  <div className="text-4xl font-black" style={{ color: s.color }}>{s.icon}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-green-700 mb-2">✓ Pros</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-700">
                      {s.pros.map((p) => <li key={p} className="leading-[1.4]">{p}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-500 mb-2">✕ Cons</div>
                    <ul className="space-y-1.5 text-[13px] text-gray-500">
                      {s.cons.map((c) => <li key={c} className="leading-[1.4]">{c}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INTERACTIVE PICKER (FRIDGE-578) ============ */}
      <IcePicker />

      {/* ============ SCENARIOS ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Which wins for you?" title="Six scenarios, six verdicts" />
          <div className="border-t border-gray-900">
            {SCENARIOS.map((s, i) => (
              <div
                key={s.who}
                className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_auto] gap-3 md:gap-5 items-start md:items-center py-4 md:py-5 border-b border-gray-900"
              >
                <div className="font-mono text-[13px] font-bold min-w-[28px] text-[#ff9951] pt-1 md:pt-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="text-[15px] md:text-[16px] font-extrabold tracking-[-0.015em] text-gray-900">{s.who}</h3>
                  <p className="text-[13px] text-gray-600 mt-0.5 leading-[1.5]">{s.reason}</p>
                </div>
                <div className="col-start-2 md:col-auto md:text-right mt-2 md:mt-0">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-white text-[12px]"
                    style={{ background: s.winner === "nugget" ? "#0469f0" : "#ff9951" }}
                  >
                    {s.winner === "nugget" ? "Nugget" : "Cube"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL — ice makers (owner 08/27/2026:
          carousels on this page are Ice Maker types, not fridges) ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Shop the category"
          title="Top ice makers"
          description="Countertop, undercounter and portable ice machines from every major retailer — both harvest styles."
          collectionType="ice-maker"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-white py-8 md:py-12 border-t border-gray-200">
        <div className="max-w-[960px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we compared" title="Methodology" />
          <div className="prose prose-sm max-w-none text-gray-700 space-y-3">
            <p>
              <strong>Manufacturer specs.</strong> Performance claims (production rate, bin
              capacity, energy draw) are drawn from currently published product spec sheets at{" "}
              <strong>Hoshizaki America</strong>, <strong>Manitowoc Ice</strong>,{" "}
              <strong>Scotsman</strong>, <strong>GE Profile Opal</strong>, and{" "}
              <strong>FirstBuild</strong>. Specs change between product generations — verify on the
              manufacturer&apos;s site for any specific model you&apos;re shopping.
            </p>
            <p>
              <strong>Energy framing.</strong> Commercial cube machines are eligible for the{" "}
              <strong>ENERGY STAR commercial ice machine</strong> certification, which sets
              maximum kWh-per-100-lb-ice thresholds by harvest type. Most countertop nugget
              appliances aren&apos;t covered by that program; their per-pound energy use is materially
              higher.
            </p>
            <p>
              <strong>Texture and melt physics.</strong> Nugget&apos;s chewable texture and faster melt
              rate come from the same property — high surface area per unit mass. That&apos;s also why
              nugget absorbs flavor and kills carbonation faster than dense cube ice.
            </p>
          </div>
        </div>
      </section>

      {/* ============ MODEL TABS — secondary carousel ============ */}
      <section className="bg-white py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="Deep dive" title="Best picks per side" />
          <div className="flex gap-2 mb-6 flex-wrap">
            {(
              [
                ["nugget", "Best nugget machines"],
                ["cube", "Best cube machines"],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => setTab(v)}
                className={[
                  "px-5 py-2.5 rounded-full font-bold text-[12px] transition-all border border-gray-900",
                  tab === v ? "bg-gray-900 text-white" : "bg-white text-gray-900 hover:bg-gray-50",
                ].join(" ")}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <ToolProductCarousel
          key={tab}
          title={tab === "nugget" ? "Nugget ice makers" : "Cube & clear-ice makers"}
          collectionType="ice-maker"
          titleMatch={tab === "nugget" ? ["nugget"] : ["clear ice", "ice cube", "cube ice", "bullet"]}
          description={
            tab === "nugget"
              ? "The chewable-ice machines — countertop nugget makers and their commercial cousins."
              : "Dense, slow-melting cube and clear-ice machines for cocktails and carbonation."
          }
        />
      </section>
    </ToolPageShell>
  )
}
